import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import ProductPage from '../components/ProductPage';
import { AmmTaskFinder } from '../components/AmmTaskFinder';
import { COMBO_KITS, COMBO_KIT_LIST } from '../data/comboKits';
import { getBoeingCoverage } from '../data/acousticCameras';

const KitCrossLinksInner = ({ kit, other }) => (
  <section className="py-14 bg-slate-50 border-t border-slate-200" data-testid="combo-kit-components">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-600 mb-3">What's in the kit</p>
      <h2 className="text-2xl sm:text-3xl font-bold text-[#1e2d3b] mb-6">Two tools, one part number</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <Link to="/products/ul101" className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-blue-300 hover:shadow-lg transition-all" data-testid="combo-kit-link-ul101">
          <p className="font-mono text-xs text-slate-500">P/N B00033 · Boeing SPL-1473</p>
          <h3 className="mt-1 text-lg font-bold text-[#1e2d3b]">UL101 Aviation Troubleshooter kit</h3>
          <p className="mt-2 text-sm text-slate-600">Receiver, transmitter, probe sets, concentrators, headset and case — see every component and replacement part.</p>
          <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 group-hover:text-orange-600 transition-colors">UL101 details <ArrowRight className="h-4 w-4" /></span>
        </Link>
        <Link to={kit.cameraPath} className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-blue-300 hover:shadow-lg transition-all" data-testid="combo-kit-link-camera">
          <p className="font-mono text-xs text-slate-500">P/N {kit.cameraId === 'h6' ? 'F00061' : 'F00042'} · COM-18702 equivalent</p>
          <h3 className="mt-1 text-lg font-bold text-[#1e2d3b]">FOTRIC {kit.cameraId === 'h6' ? 'H6' : 'H4mini'} acoustic imaging camera</h3>
          <p className="mt-2 text-sm text-slate-600">Full specifications, imaging modes, software and the model comparison tool.</p>
          <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 group-hover:text-orange-600 transition-colors">Camera details <ArrowRight className="h-4 w-4" /></span>
        </Link>
      </div>
      <p className="mt-6 text-sm text-slate-500">
        Looking at the other kit? <Link to={`/products/combo-kits/${other.slug}`} className="font-semibold text-blue-700 hover:underline" data-testid="combo-kit-switch">Compare with the {other.shortName}</Link>
      </p>
    </div>
  </section>
);

const KitCrossLinks = ({ kit }) => <KitCrossLinksInner kit={kit} other={COMBO_KIT_LIST.find((k) => k.slug !== kit.slug)} />;

const ComboKitPage = ({ onRequestDemo, onRequestQuote }) => {
  const { slug } = useParams();
  const kit = COMBO_KITS[slug];
  if (!kit) return <Navigate to="/products/combo-kits" replace />;
  return (
    <ProductPage
      {...kit}
      buyNow={kit.partNumber}
      boeingCoverage={getBoeingCoverage(kit.cameraId)}
      extraSections={<><KitCrossLinks kit={kit} /><AmmTaskFinder tool="COM-18702" /></>}
      onRequestDemo={onRequestDemo}
      onRequestQuote={onRequestQuote}
    />
  );
};

export default ComboKitPage;
